//
// Copyright 2024 DXOS.org
//

import React from 'react';

import { Obj, Type } from '@dxos/echo';
import { Doc } from '@dxos/echo-doc';
import { useTextEditor } from '@dxos/react-ui-editor';
import * as Button from '@dxos/react-ui/Button';
import * as Field from '@dxos/react-ui/Field';
import * as Hooks from '@dxos/react-ui/Hooks';
import * as Input from '@dxos/react-ui/Input';
import * as ScrollArea from '@dxos/react-ui/ScrollArea';
import * as Util from '@dxos/react-ui/Util';
import { mapSchemaToFields } from '@dxos/schema';
import { automerge, createBasicExtensions, createMarkdownExtensions, createThemeExtensions } from '@dxos/ui-editor';

const MAX_RENDERED_COUNT = 80;

export type ItemListProps<T> = { objects: T[] } & Pick<ItemProps<T>, 'debug' | 'onDelete'>;

export const ItemList = Util.composable<HTMLDivElement, ItemListProps<Obj.Any>>(
  ({ objects, debug, onDelete, ...props }, forwardedRef) => {
    return (
      <ScrollArea.Root {...Util.composableProps(props)} ref={forwardedRef}>
        <ScrollArea.Viewport>
          {objects
            .slice(0, MAX_RENDERED_COUNT)
            .map(
              (object) =>
                (debug && <DebugItem key={object.id} object={object} onDelete={onDelete} />) || (
                  <Item key={object.id} object={object} onDelete={onDelete} />
                ),
            )}
          {objects.length > MAX_RENDERED_COUNT && (
            <div className='text-xs text-gray-400'>({objects.length - MAX_RENDERED_COUNT} more items)</div>
          )}
        </ScrollArea.Viewport>
      </ScrollArea.Root>
    );
  },
);

ItemList.displayName = 'ItemList';

const labelProps = 'shrink-0 w-20 text-right text-primary-500 px-2 py-[2px]';

export type ItemProps<T> = {
  object: T;
  debug?: boolean;
  onDelete: (id: string) => void;
};

// TODO(burdon): Use ui list with key nav/selection.
// TODO(burdon): Toggle options to show deleted.
export const Item = ({ object, onDelete }: ItemProps<Obj.Any>) => {
  const type = Obj.getType(object);
  if (!type) {
    return <DebugItem object={object} onDelete={onDelete} />;
  }

  // TODO(burdon): Get additional metadata.
  const props = mapSchemaToFields(Type.getSchema(type));

  // TODO(burdon): [API]: Type check?
  const getValue = (object: Obj.Any, prop: string) => object[prop];
  const setValue = (object: Obj.Any, prop: string, value: any) =>
    Obj.update(object, (object) => (object[prop] = value));

  return (
    <div className='flex m-1 p-2 border dx-hover'>
      <div className='flex flex-col grow overflow-hidden gap-2'>
        {props.map(({ property, type }) => (
          <div key={property} className='flex'>
            {/* TODO(burdon): Check if editable or meta prop (e.g., id). */}
            {property === 'id' && (
              <Field.Root>
                <Field.Label classNames={labelProps}>{property}</Field.Label>
                <div className='font-mono text-xs py-1'>{getValue(object, property).slice(0, 8)}</div>
              </Field.Root>
            )}
            {type === 'boolean' && (
              <Field.Root>
                <Field.Label classNames={labelProps}>{property}</Field.Label>
                <Input.Checkbox
                  checked={(object as any)[property]}
                  onCheckedChange={({ checked: state }) => setValue(object, property, !!state)}
                />
              </Field.Root>
            )}
            {property !== 'id' && type === 'string' && (
              <Field.Root>
                <Field.Label classNames={labelProps}>{property}</Field.Label>
                <Editor object={object} prop={property} />
              </Field.Root>
            )}
          </div>
        ))}
      </div>

      {/* TODO(burdon): Check if mutable. */}
      <div className='flex flex-col shrink-0'>
        <Button.Root
          icon='ph--x--regular'
          iconOnly
          label='Delete'
          onClick={() => onDelete(object.id)}
          variant='ghost'
        />
      </div>
    </div>
  );
};

const Editor = ({ object, prop }: { object: Obj.Any; prop: string }) => {
  const themeMode = Hooks.useThemeMode();
  const { parentRef } = useTextEditor(() => {
    return {
      initialValue: object[prop],
      extensions: [
        createBasicExtensions(),
        createMarkdownExtensions(),
        createThemeExtensions({ themeMode, slots: { content: { className: 'p-0' } } }),
        automerge(Doc.createAccessor(object, [prop])),
      ],
    };
  }, []);

  return <div ref={parentRef} className='grow' />;
};

// TODO(burdon): Add metadata.
export const DebugItem = ({ object, onDelete }: Pick<ItemProps<Obj.Any>, 'object' | 'onDelete'>) => {
  const meta = Obj.getMeta(object);
  const deleted = JSON.stringify(object).indexOf('@deleted') !== -1; // TODO(burdon): [API] Missing API.
  return (
    <div className='flex w-full px-1.5 py-1 text-sm font-thin font-mono'>
      <pre className='grow'>{JSON.stringify({ id: object.id.slice(0, 8), deleted, ...meta }, undefined, 2)}</pre>
      <Button.Root icon='ph--x--regular' variant='ghost' iconOnly onClick={() => onDelete(object.id)} label='Delete' />
    </div>
  );
};
